// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/useAskV2PlanProgress.test.ts
import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  ASK_V2_PLAN_SKIPPED_DETAIL,
  ASK_V2_PLAN_STARTING_ID,
  buildAskV2PlanProgress,
  readLatestPlanStepsFromMessages,
} from "./buildRailProgressFromPlan";
import {
  checkProgressFromMessages,
  nestCheckProgress,
} from "./checkProgressRows";
import {
  type AskV2ReviewProgressMode,
  messagesAfterLatestReviewStart,
} from "./collectRailContent";
import { type AskV2RailProgressItem } from "./types";

/**
 * The review rail's rows, read off the checklist warhol writes with
 * `write_plan`.
 *
 * The rows used to be a client-side timer script on a three-minute budget, so
 * they advanced whether or not the review did. They now report only what the
 * model put in its own plan, which is why a step can sit `pending` for a long
 * time: that is the review's real position.
 *
 * The rows are derived from persisted messages, so a reload or a second tab
 * rebuilds the same rail instead of restarting it -- the old script kept its
 * clock in per-tab `sessionStorage` and began again from the first step.
 */
export function useAskV2PlanProgress(input: {
  messages?: readonly AskPunttMessage[];
  mode: AskV2ReviewProgressMode;
}): {
  items: AskV2RailProgressItem[];
  showShell: boolean;
} {
  const { t } = useTranslation("ui");
  const { messages, mode } = input;

  // Scoped to the same cycle the plan is, and for the same reason: the pending
  // row of the PREVIOUS review keeps its checks until the answer replaces the
  // row in place, so a read across the whole thread would tick the last
  // review's checks under this one's plan.
  const cycle = useMemo(
    () => messagesAfterLatestReviewStart(messages ?? []),
    [messages],
  );

  const steps = useMemo(
    // Scoped to the cycle now running, not to the whole thread: `mode` comes
    // from the review's status, so a chat's SECOND review is already `running`
    // while that turn has written no plan. Reading the newest plan anywhere in
    // the thread then drew the PREVIOUS review's checklist, every row ticked,
    // over a review that had done nothing -- the same false progress the timer
    // script was removed for. With no plan in this cycle the honest "Getting
    // started" row below renders instead.
    () => readLatestPlanStepsFromMessages(cycle),
    [cycle],
  );

  // What warhol has settled so far this turn, off the pending row christo keeps
  // rewriting. Empty for every turn that is not warhol's, and for a warhol turn
  // whose progress row could not be read -- in both cases the rail draws
  // exactly the plan rows it drew before this existed.
  const checkProgress = useMemo(
    () => checkProgressFromMessages(cycle),
    [cycle],
  );

  const items = useMemo(() => {
    if (mode === "idle") return [];
    const planned = buildAskV2PlanProgress({
      steps,
      completed: mode === "completed",
    });
    if (planned.length > 0) {
      // The builder is pure and i18n-free, so the skipped row arrives marked.
      // Translate it here, where `t` lives, or the rail renders untranslated
      // English in every locale but English.
      return planned.map((item) =>
        item.detail === ASK_V2_PLAN_SKIPPED_DETAIL
          ? { ...item, detail: t("askV2.rail.progressSteps.skipped") }
          : item,
      );
    }
    if (mode !== "running") return [];
    // No plan yet. A turn that needs one tool call never writes one, and a
    // planned turn is still deciding, so this is the only row that may be
    // shown without the backend having reported it -- and it claims nothing
    // but that the turn has started.
    return [
      {
        id: ASK_V2_PLAN_STARTING_ID,
        label: t("askV2.rail.progressSteps.starting"),
        status: "active" as const,
      },
    ];
  }, [mode, steps, t]);

  // The checks ride the row the turn is working on. The tally's copy is built
  // here, where `t` lives, for the same reason the skipped row's is: the
  // nesting module is pure and i18n-free.
  const rows = useMemo(
    () =>
      nestCheckProgress(items, checkProgress, (tally) =>
        t("askV2.rail.progressSteps.verified", {
          verified: tally.verified,
          total: tally.total,
        }),
      ),
    [items, checkProgress, t],
  );

  return { items: rows, showShell: mode !== "idle" };
}
